import React from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";

function CarePlanDialog({
  open,
  patients,
  selectedPatientId,
  selectedPatientName,
  formData,
  onChange,
  onCancel,
  onSubmit,
}) {
  const handleFieldChange = (name, value) => {
    onChange({
      ...formData,
      [name]: value,
    });
  };

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="md" fullWidth>
      <DialogTitle>
        Create Care Plan
      </DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2 }}>
          {!selectedPatientId && (
            <FormControl fullWidth margin="normal">
              <InputLabel id="care-plan-patient-label">Patient</InputLabel>
              <Select
                labelId="care-plan-patient-label"
                id="care-plan-patient-select"
                label="Patient"
                value={formData.patient_id || ""}
                onChange={(e) => handleFieldChange("patient_id", e.target.value)}
              >
                {patients.map((patient) => (
                  <MenuItem key={patient.id} value={patient.id}>
                    {patient.name} - MRN: {patient.mrn}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <TextField
            fullWidth
            label="Care Plan Title"
            value={formData.title || ""}
            onChange={(e) => handleFieldChange("title", e.target.value)}
            margin="normal"
            required
            placeholder="e.g., Diabetes Management Plan, Post-Discharge Care Plan"
            aria-label="Care plan title"
            data-field-name="care_plan_title"
            data-testid="care-plan-title-input"
          />

          <TextField
            fullWidth
            label="Description"
            value={formData.description || ""}
            onChange={(e) => handleFieldChange("description", e.target.value)}
            margin="normal"
            multiline
            rows={3}
            placeholder="Overall care plan summary"
            aria-label="Care plan description"
            data-field-name="care_plan_description"
            data-testid="care-plan-description-input"
          />

          <TextField
            fullWidth
            label="Goals"
            value={formData.goals || ""}
            onChange={(e) => handleFieldChange("goals", e.target.value)}
            margin="normal"
            multiline
            rows={2}
            required
            placeholder="e.g., Improve A1C to <7%, Reduce blood pressure to <130/80"
            aria-label="Care plan goals"
            data-field-name="care_plan_goals"
            data-testid="care-plan-goals-input"
          />

          <TextField
            fullWidth
            label="Interventions"
            value={formData.interventions || ""}
            onChange={(e) => handleFieldChange("interventions", e.target.value)}
            margin="normal"
            multiline
            rows={2}
            required
            placeholder="e.g., Medication management, lifestyle counseling, monthly check-ins"
            aria-label="Care plan interventions"
            data-field-name="care_plan_interventions"
            data-testid="care-plan-interventions-input"
          />

          <TextField
            fullWidth
            label="Created By"
            value={formData.created_by || ""}
            onChange={(e) => handleFieldChange("created_by", e.target.value)}
            margin="normal"
            required
            placeholder="Your name"
            aria-label="Care plan creator"
            data-field-name="care_plan_created_by"
            data-testid="care-plan-created-by-input"
          />

          <TextField
            fullWidth
            label="Start Date"
            type="date"
            value={formData.start_date || ""}
            onChange={(e) => handleFieldChange("start_date", e.target.value)}
            margin="normal"
            InputLabelProps={{ shrink: true }}
            aria-label="Care plan start date"
            data-field-name="care_plan_start_date"
            data-testid="care-plan-start-date-input"
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>Cancel</Button>
        <Button onClick={onSubmit} variant="contained" color="primary">
          Create Care Plan
        </Button>
      </DialogActions>
    </Dialog>
  );
}

CarePlanDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  patients: PropTypes.array.isRequired,
  selectedPatientId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  selectedPatientName: PropTypes.string,
  formData: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
};

CarePlanDialog.defaultProps = {
  selectedPatientId: null,
  selectedPatientName: "",
};

export default CarePlanDialog;
